import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Spin } from 'antd';
import { api, resolveAccessToken } from '../services/api';
import AccessDeniedPage from '../pages/AccessDenied/AccessDeniedPage';

const GOOGLE_LINK_SESSION_KEY = 'google_account_linked';

const AuthGate = ({ children }) => {
    const { user, isAuthenticated, isLoading: authLoading, getAccessTokenSilently } = useAuth0();
    const [authState, setAuthState] = useState('checking'); // 'checking' | 'success' | 'denied'
    const hasAttemptedLink = useRef(false);

    useEffect(() => {
        const linkGoogleAccount = async () => {
            // Don't proceed if not authenticated or already attempted
            if (!isAuthenticated || !user || hasAttemptedLink.current) {
                return;
            }

            // Check if already linked in this session
            const sessionLinked = sessionStorage.getItem(GOOGLE_LINK_SESSION_KEY);
            if (sessionLinked === 'true') {
                setAuthState('success');
                return;
            }

            // Mark as attempted to prevent duplicate calls
            hasAttemptedLink.current = true;

            try {
                const token = await resolveAccessToken(getAccessTokenSilently);

                await api.linkGoogleAccount({
                    email: user.email,
                    googleAuthUserId: user.sub
                }, token);

                console.log('Google account linked successfully');

                // Mark as linked in session storage (cleared on browser close)
                sessionStorage.setItem(GOOGLE_LINK_SESSION_KEY, 'true');
                setAuthState('success');

            } catch (err) {
                console.error('Error linking Google account:', err);
                setAuthState('denied');
            }
        };

        if (!authLoading) {
            linkGoogleAccount();
        }
    }, [isAuthenticated, user, authLoading, getAccessTokenSilently]);

    // Show loading while Auth0 is loading or while checking Google link
    if (authLoading || authState === 'checking') {
        return (
            <div className="min-h-screen bg-gradient-to-l from-blue-50 to-blue-100 flex items-center justify-center">
                <Spin size="large" tip="Authenticating..." />
            </div>
        );
    }

    // Show access denied screen if Google link failed
    if (authState === 'denied') {
        return <AccessDeniedPage />;
    }

    // Render children only if authentication succeeded
    return children;
};

export default AuthGate;
